import Messages from './messages.js'

/**
 * Represents a device detail object
 * 
 * @typedef {Object} Device
 * @property {Object} wifi - Wifi device detail
 * @property {Object} ethernet - Ethernet device detail
 */

/**
 * Represents a access point
 * 
 * @typedef {Object} AccessPoint
 * @property {string} bssid
 * @property {string} ssid
 * @property {string} channel
 * @property {string} rate
 * @property {string} security
 * @property {boolean} in_use
 * @property {number} bars
 */

/**
 * Represents a connection profile
 * 
 * @typedef {Object} ConnectionProfile
 * @property {string} name - Name of profile
 * @property {string} ssid - SSID of profile
 * @property {string} id - Profile uuid
 */

export default class WifiClient {

  constructor(socket) {
    this.socket = socket
  }

  /**
   * Get connection status
   * 
   * @param {boolean} forceRecheck
   * @returns {Promise<("none"|"portal"|"limited"|"full"|"unknown")>}
   */
  async connectionStatus(forceRecheck) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_ConnectionStatus, forceRecheck);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return response.data
  }

  /**
   * Remove profile
   * 
   * @param {string} connectionId
   * @returns {Promise<void>}
   */
  async removeProfile(connectionId) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_RemoveProfile, connectionId);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return
  }

  /**
   * Connect profile
   * 
   * @param {string} ssid 
   * @param {string} password 
   * @returns {Promise<object>}
   */
  async connectProfile(ssid, password) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_ConnectProfile, ssid, password);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return response.data
  }

  /**
   * Get connection profiles
   * 
   * @param {boolean} active 
   * @returns {Promise<Array<ConnectionProfile>>}
   */
  async getConnectionProfiles(active) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_GetConnectionProfiles, active);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return response.data
  }

  /**
   * Get access points
   * 
   * @param {boolean} inUse 
   * @returns {Promise<Array<AccessPoint>>}
   */
  async getAccessPoints(inUse) {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_GetAccessPoints, inUse);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return response.data
  }

  /**
   * Get device details
   * 
   * @returns {Promise<Device>} 
   */
  async devices() {
    let response;
    try {
      response = await this.socket.emitWithAck(Messages.Wifi_GetDevices);
    } catch (error) {
      console.error(`WifiClient : ${error}`)
      throw new Error('Socket Error')
    }
    if (!response.success) {
      const error = `${response.error.code}:${response.error.message}`
      throw new Error(error)
    }
    return response.data
  }
}